/**
 * <VoiceRecorderSheet> — enregistrer un message vocal, le réécouter, puis l'envoyer (template).
 *
 * La surface de l'enregistreur, à parité avec l'ancien (`AudioRecordingModal` +
 * `AudioRecordingContainer`, `chatMode`) : enregistrer, mettre en pause et reprendre, la durée qui
 * court (12 minutes au plus), réécouter, réenregistrer, envoyer, et fermer pour abandonner. Ce que
 * l'ancien disait par une boîte (micro refusé, durée maximale atteinte) se dit dans la feuille.
 *
 * Une feuille basse sur un téléphone, une carte au milieu sur un grand écran (`<Modal>`). En haut,
 * le compteur (`<RecorderClock>`, qui porte son propre décompte : la feuille ne se redessine pas
 * chaque seconde) et la ligne d'état ; une fois enregistré, le vocal tel qu'il partira, dans la
 * bulle de l'élève, avec son bouton de lecture. En pied, deux gestes au plus :
 *  - `idle` : « Enregistrer » ;
 *  - `requesting` : « Enregistrer » qui tourne (le micro est demandé) ;
 *  - `recording` / `paused` : « Pause » ou « Reprendre », et « Terminer » ;
 *  - `recorded` : « Réenregistrer », et « Envoyer » ;
 *  - `denied` : « Ouvrir les réglages » quand l'écran sait les ouvrir, sinon « Réessayer » ;
 *  - `error` : « Réessayer ».
 * La croix de la feuille abandonne, à toute étape.
 *
 * Contrôlée : l'état, la durée, la lecture et l'envoi arrivent en props, chaque geste remonte. Le
 * moteur est `engine/voice/useVoiceRecorder` ; la feuille ne connaît ni le micro ni le fichier.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { MessageBubble } from "../../molecules/MessageBubble"
import { Modal } from "../../molecules/Modal"
import { type UIVoiceRecorderNotice, type UIVoiceRecorderPhase } from "../../../ports/primary/ui/types/studentVoice"
import { DEFAULT_MAX_DURATION_MS, RecorderClock, recorderNoticeText } from "../../molecules/RecorderClock"

export type { UIVoiceRecorderNotice, UIVoiceRecorderPhase }

/** La réécoute de ce qui vient d'être enregistré. */
export type VoiceRecorderPlayback = {
    playing: boolean
    /** De 0 à 1. */
    progress: number
    onToggle: () => void
}

export type VoiceRecorderSheetProps = {
    visible: boolean
    /** La croix : abandonner (l'écran oublie l'enregistrement). */
    onClose: () => void
    /** « Message vocal » par défaut. */
    title?: string
    phase: UIVoiceRecorderPhase
    /** La durée figée au dernier changement, en ms ; `recorded` : celle du vocal. */
    durationMs: number
    /** Le début du segment en cours (`Date.now()`) : le compteur court tout seul. */
    runningSince?: number | null
    /** Le plafond, écrit à côté du compteur. Défaut : 12 minutes. */
    maxDurationMs?: number
    notice?: UIVoiceRecorderNotice | null
    /** Les mots d'une fin demandée trop tôt (`notice: "too_short"`), quand l'appelant a une durée minimale. */
    tooShortMessage?: string
    /** `error` : ce qui s'est passé, en mots pour l'utilisateur. Défaut : la phrase de la feuille. */
    errorMessage?: string
    onStart: () => void
    onPause: () => void
    onResume: () => void
    onStop: () => void
    /** L'arrêt est demandé, le fichier s'écrit : « Terminer » tourne. */
    stopping?: boolean
    /** `recorded` : réécouter. */
    playback?: VoiceRecorderPlayback
    /** `recorded` : tout reprendre. */
    onRedo: () => void
    /** `recorded` : envoyer. Absent : pas de bouton (l'écran envoie autrement). */
    onSend?: () => void
    /** « Envoyer » par défaut. */
    sendLabel?: string
    /** L'envoi part : « Envoyer » tourne. */
    sending?: boolean
    /** `denied` : ouvrir les réglages de l'appareil, quand le système ne redemande plus. */
    onOpenSettings?: () => void
    testID?: string
}

const TEXTS = {
    title: "Message vocal",
    idle: "Appuie sur « Enregistrer » et parle.",
    requesting: "On demande l'accès au micro…",
    recording: "Enregistrement en cours",
    paused: "En pause",
    recorded: "Réécoute ton message avant de l'envoyer.",
    deniedTitle: "Le micro n'est pas autorisé",
    denied: "Autorise l'accès au micro pour enregistrer un message vocal.",
    deniedSettings: "Autorise l'accès au micro dans les réglages, puis reviens ici.",
    error: "L'enregistrement n'a pas marché. Réessaie.",
    record: "Enregistrer",
    pause: "Pause",
    resume: "Reprendre",
    stop: "Terminer",
    redo: "Réenregistrer",
    send: "Envoyer",
    settings: "Ouvrir les réglages",
    retry: "Réessayer",
    voice: "Ton message vocal",
}

/** « 0:42 », la durée d'un vocal comme le fil l'écrit. */
function voiceLabel(ms: number): string {
    const total = Math.round(ms / 1000)
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}

export function VoiceRecorderSheet(props: VoiceRecorderSheetProps) {
    const {
        visible,
        onClose,
        title = TEXTS.title,
        phase,
        durationMs,
        runningSince,
        maxDurationMs = DEFAULT_MAX_DURATION_MS,
        notice,
        tooShortMessage,
        errorMessage,
        onStart,
        onPause,
        onResume,
        onStop,
        stopping = false,
        playback,
        onRedo,
        onSend,
        sendLabel = TEXTS.send,
        sending = false,
        onOpenSettings,
        testID,
    } = props

    const live = phase === "recording" || phase === "paused"

    const footer = (() => {
        switch (phase) {
            case "idle":
            case "requesting":
                return (
                    <Button
                        label={TEXTS.record}
                        iconName="microphone"
                        onPress={onStart}
                        loading={phase === "requesting"}
                        fullWidth
                    />
                )
            case "recording":
            case "paused":
                return (
                    <Row gap="3">
                        <View style={styles.half}>
                            <Button
                                variant="outline"
                                label={phase === "recording" ? TEXTS.pause : TEXTS.resume}
                                iconName={phase === "recording" ? "pause" : "microphone"}
                                onPress={phase === "recording" ? onPause : onResume}
                                disabled={stopping}
                                fullWidth
                            />
                        </View>
                        <View style={styles.half}>
                            <Button label={TEXTS.stop} iconName="check" onPress={onStop} loading={stopping} fullWidth />
                        </View>
                    </Row>
                )
            case "recorded":
                return (
                    <Row gap="3">
                        <View style={styles.half}>
                            <Button
                                variant="outline"
                                label={TEXTS.redo}
                                iconName="redo"
                                onPress={onRedo}
                                disabled={sending}
                                fullWidth
                            />
                        </View>
                        {onSend != null && (
                            <View style={styles.half}>
                                <Button
                                    label={sendLabel}
                                    iconName="telegram-plane"
                                    onPress={onSend}
                                    loading={sending}
                                    fullWidth
                                />
                            </View>
                        )}
                    </Row>
                )
            case "denied":
                return (
                    <Button
                        label={onOpenSettings != null ? TEXTS.settings : TEXTS.retry}
                        iconName={onOpenSettings != null ? "cog" : "redo"}
                        onPress={onOpenSettings ?? onStart}
                        fullWidth
                    />
                )
            case "error":
                return <Button label={TEXTS.retry} iconName="redo" onPress={onStart} fullWidth />
            default:
                return null
        }
    })()

    const noticeText = recorderNoticeText(notice, maxDurationMs, tooShortMessage)

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={title}
            fullScreenOnSmall
            sizeToContent
            footer={footer}
            testID={testID}
        >
            {phase === "denied" ? (
                <Stack gap="3" align="center" style={styles.body}>
                    <Icon name="microphone-slash" size={32} color="danger" />
                    <Text size="text-xl" weight="bold" color="text-strong" center>
                        {TEXTS.deniedTitle}
                    </Text>
                    <Text size="text-md" color="text-muted" center>
                        {onOpenSettings != null ? TEXTS.deniedSettings : TEXTS.denied}
                    </Text>
                </Stack>
            ) : phase === "recorded" ? (
                <Stack gap="4" style={styles.body}>
                    {noticeText != null && <AlertCard severity="info" message={noticeText} />}
                    <Text size="text-md" color="text-muted" center>
                        {TEXTS.recorded}
                    </Text>
                    <View style={styles.bubble} accessibilityLabel={TEXTS.voice}>
                        <MessageBubble
                            face="own"
                            tail
                            attachment={{ kind: "voice", uri: "", durationLabel: voiceLabel(durationMs) }}
                            voicePlaying={playback?.playing ?? false}
                            voiceProgress={playback?.progress ?? 0}
                            onToggleVoice={playback?.onToggle}
                        />
                    </View>
                </Stack>
            ) : (
                <Stack gap="3" style={styles.body}>
                    {phase === "error" && <AlertCard severity="alert" message={errorMessage ?? TEXTS.error} />}
                    {noticeText != null && <AlertCard severity="warning" message={noticeText} />}
                    <RecorderClock
                        durationMs={durationMs}
                        runningSince={phase === "recording" && !stopping ? runningSince : null}
                        maxDurationMs={maxDurationMs}
                    />
                    <Row gap="2" align="center" justify="center" style={styles.status}>
                        {phase === "recording" && <View style={styles.dot} />}
                        <Text size="text-md" weight={live ? "semibold" : "regular"} color={statusColor(phase)} center>
                            {statusText(phase)}
                        </Text>
                    </Row>
                </Stack>
            )}
        </Modal>
    )
}

function statusText(phase: UIVoiceRecorderPhase): string {
    if (phase === "recording") return TEXTS.recording
    if (phase === "paused") return TEXTS.paused
    if (phase === "requesting") return TEXTS.requesting
    return TEXTS.idle
}

function statusColor(phase: UIVoiceRecorderPhase) {
    return phase === "recording" ? ("text-danger" as const) : ("text-muted" as const)
}

const DOT = 10

const styles = StyleSheet.create((theme) => ({
    body: { paddingVertical: theme.spacing["4"] },
    status: { minHeight: 24 },
    dot: { width: DOT, height: DOT, borderRadius: DOT / 2, backgroundColor: theme.colors.danger },
    half: { flex: 1 },
    /**
     * Le vocal tel qu'il partira, au milieu de la feuille, sous la phrase centrée qui l'annonce : calé
     * à droite comme dans le fil, il semblait posé de travers. Vu par Wajih au bilan du tuteur (03/11),
     * qui n'ouvre plus cette feuille ; vrai partout où elle s'ouvre (messagerie, chat du live, copie).
     */
    bubble: { alignItems: "center" },
}))
